import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { MessageCircleQuestion, Send, History } from "lucide-react";

const MODULES = [
  [1, "Module 1 — Platform Overview and the Founder Journey"],
  [2, "Module 2 — Steps 1–3"],
  [3, "Module 3 — Steps 4–5"],
  [4, "Module 4 — Step 5: Your Score and Report"],
  [5, "Module 5 — Financial Analyst Support"],
  [6, "Module 6 — Masterclasses and Cohorts"],
  [7, "Module 7 — Handling Difficult Situations"],
  [8, "Module 8 — The RM Portal"],
  [9, "Module 9 — Outsourced Partner Referrals"],
  [10, "Module 10 — Client and Customer Etiquette"],
  [11, "Module 11 — Your Practical Assessment"],
];

export const QuestionBoard = () => {
  const [question, setQuestion] = useState("");
  const [moduleId, setModuleId] = useState("");
  const [data, setData] = useState({ open: [], history: [] });
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = () => api.get("/rm/training/questions").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/rm/training/questions", { question, module_id: Number(moduleId) });
      toast.success("Question submitted for Monday's check-in");
      setQuestion(""); setModuleId("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't submit — try again"); }
    setBusy(false);
  };

  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="rm-question-board">
      <p className="flex items-center gap-2 text-sm font-extrabold text-[#0A2540]">
        <MessageCircleQuestion size={17} className="text-[#0A8B85]" /> Monday Check-in Question Board
      </p>
      <p className="mt-1 text-xs text-slate-500">
        Submit questions from your training any time — they'll be discussed at the next Monday check-in. Only you and the Clinq team can see them.
      </p>

      <textarea rows={3} value={question} onChange={(e) => setQuestion(e.target.value)}
        placeholder="What would you like to discuss on Monday?"
        className="mt-4 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0A8B85]"
        data-testid="rm-question-input" />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select value={moduleId} onChange={(e) => setModuleId(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-[#0A8B85]"
          data-testid="rm-question-module-select">
          <option value="">Which module does it relate to?</option>
          {MODULES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={submit} disabled={busy || !question.trim() || !moduleId}
          className="flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white hover:bg-[#087a74] disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="rm-question-submit-btn">
          <Send size={13} /> Submit question
        </button>
      </div>

      {data.open.length > 0 && (
        <div className="mt-5">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Waiting for Monday ({data.open.length})</p>
          <div className="mt-2 space-y-2" data-testid="rm-question-open-list">
            {data.open.map((q) => (
              <div key={q.question_id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`rm-question-${q.question_id}`}>
                <p className="text-sm text-slate-700">{q.question}</p>
                <p className="mt-0.5 text-[10px] text-slate-400">
                  {q.module_title} · submitted {q.created_at?.slice(0, 10)} · for Monday {q.monday_date}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <button onClick={() => setShowHistory((s) => !s)}
        className="mt-4 flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-[#0A8B85]"
        data-testid="rm-question-history-toggle">
        <History size={12} /> Discussion history ({data.history.length})
      </button>
      {showHistory && (
        <div className="mt-2 space-y-2" data-testid="rm-question-history-list">
          {data.history.length === 0 ? (
            <p className="text-xs text-slate-400">Nothing discussed yet.</p>
          ) : data.history.map((q) => (
            <div key={q.question_id} className="rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2">
              <p className="text-sm text-slate-600">{q.question}</p>
              <p className="mt-0.5 text-[10px] text-slate-400">
                {q.module_title} · discussed {q.discussed_at?.slice(0, 10)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
